import React, {useEffect, useMemo, useState} from 'react';
import { inject, observer } from "mobx-react";
import Modal from './../../components/modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SupplierForm from '../../components/supplier-form';
import {
    faArrowDown,
    faArrowUp,
    faPencilAlt,
    faSpinner,
    faWindowClose,
} from "@fortawesome/free-solid-svg-icons";
import Switch from "react-switch";

import FilterInputField from "../../components/filter-input-field";
import PaginationControls from "../../components/pagination-controls";
import Checkbox from "../../components/checkbox";
import { fetch } from '../../utils/api-wrapper';
import { downloadRowsAsXlsx } from '../../utils/excel-export';
import { imputationDocumentId } from '../../pages/review-expense/utils/contract-expense-json';
import { useSearchParams } from "react-router-dom";
import SupplierImportHelp from '../../components/supplier-import-help';
import ImportResultModal from '../../components/import-result-modal';
import Utils from '../../utils/utils';

const supplierImportSummarySpec = [
    { key: 'suppliers_created', labelKey: 'suppliers_import_summary_created' },
    { key: 'suppliers_updated', labelKey: 'suppliers_import_summary_updated' },
    { key: 'rows_failed', labelKey: 'suppliers_import_summary_failed' },
];

const objectIdValue = (value) => imputationDocumentId({ _id: value }) || value || "";

const dateValue = (value) => value?.$date ?? value ?? "";

const listValue = (items = []) => (Array.isArray(items) ? items : [])
    .filter(Boolean)
    .join("; ");

export const adminSupplierExportRow = (supplier = {}, clientName = "") => ({
    id: objectIdValue(supplier._id),
    id_client: objectIdValue(supplier.id_client),
    client_name: clientName,
    supplier_number: supplier.supplier_number ?? "",
    vendor_number: supplier.vendor_number ?? "",
    names: listValue(supplier.names ?? []),
    primary_name: supplier.names?.[0] ?? "",
    gl_code: supplier.gl_code ?? "",
    cost_type: supplier.cost_type ?? "",
    IsBillable: Boolean(supplier.IsBillable),
    TaxCode: supplier.TaxCode ?? supplier.tax_code ?? "",
    budget: supplier.budget ?? "",
    active: supplier.active !== false,
    need_moderation: Boolean(supplier.need_moderation),
    address: supplier.address ?? "",
    street: supplier.street ?? "",
    city: supplier.city ?? "",
    state: supplier.state ?? "",
    country: supplier.country ?? "",
    zipcode: supplier.zipcode ?? "",
    payment_terms: supplier.payment_terms ?? "",
    currency: supplier.currency ?? "",
    nx_unit: supplier.nx_unit ?? "",
    supplier_group: supplier.supplier_group ?? "",
    reference: supplier.reference ?? "",
    email: supplier.email ?? "",
    phone_number: supplier.phone_number ?? "",
    url: supplier.url ?? "",
    payeeId: supplier.payeeId ?? "",
    payeeIndex: supplier.payeeIndex ?? "",
    actorId: supplier.actorId ?? "",
    matterId: supplier.matterId ?? "",
    payee_type: supplier.payee_type ?? "",
    payee_category: supplier.payee_category ?? "",
    bank: supplier.bank ?? "",
    ap_gl_nat: supplier.ap_gl_nat ?? "",
    expense_gl_nat: supplier.expense_gl_nat ?? "",
    tax_num: supplier.tax_num ?? "",
    created_at: dateValue(supplier.created_at),
    updated_at: dateValue(supplier.updated_at),
});

const AdminSuppliers = ({ data }) => {
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [suppliers, setSuppliers] = useState([]);
    const [importFile, setImportFile] = useState(null);
    const [showImportModal, setShowImportModal] = useState(false);
    const [supplierImportResult, setSupplierImportResult] = useState(null);
    const [pageSize, setPageSize] = useState(20);
    const [filterValue, setFilterValue] = useState("");
    const [sorting, setSorting] = useState([{ "id": "created_at", "desc": true }]);

    const [page, setPage] = useState(1);
    const [totalRowCount, setTotalRowCount] = useState(0);
    const [showSupplierModal, setShowSupplierModal] = useState(false);
    const [updatedSupplier, setUpdatedSupplier] = useState({});
    const [suppliersDeleteSelection, setSuppliersDeleteSelection] = useState([]);
    const [showDeleteSupplierModal, setShowDeleteSupplierModal] = useState(false);
    // XPEN-3267 — hidden for tenants whose vendors are governed in their ERP.
    // Export and Delete are unaffected; the API rejects the writes regardless.
    const canCreateSuppliers = Utils.clientAllowsSupplierCreate(data);
    const canImportSuppliers = Utils.clientAllowsSupplierImport(data);
    const [saving, setSaving] = useState(false);
    const [exporting, setExporting] = useState(false);
    
    const [params, setParams] = useSearchParams();

    const fetchSupplierForAdminEdit = (supplierId) => {
        const filters = encodeURIComponent(JSON.stringify([{id: "supplier_id", value: supplierId}]));
        fetch(`/suppliers/?filters=${filters}&sorting=[]&start=0&page_size=1`, "GET", {}, response => {
            const supplier = response.data?.suppliers?.[0];
            if (supplier) {
                setUpdatedSupplier(supplier);
                setShowSupplierModal(true);
            }
            setParams({});
        });
    };

    useEffect(() => {
        const supplierId = params.get("supplier_id")
        if (supplierId) {
            fetchSupplierForAdminEdit(supplierId);
        }
    }, [params]);
    
    useEffect(() => {
        setPage(1);
        refreshSuppliers();
    }, [pageSize, sorting, filterValue]);

    useEffect(() => {
        refreshSuppliers();
    }, [page]);

    const refreshSuppliers = () => {
        let start = (page - 1) * pageSize;
        const filters = [];
        if (filterValue && filterValue.trim() !== "") {
            filters.push({ id: "search", value: filterValue.trim() });
        }
        const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters ?? []))}&sorting=${encodeURIComponent(JSON.stringify(sorting ?? []))}&start=${encodeURIComponent(start)}&page_size=${pageSize}`;
        fetch(`/suppliers/${searchData}`, "GET", {},

            response => {
                let fetchedSuppliers = response.data.suppliers;
                setSuppliers(fetchedSuppliers);
                setTotalRowCount(response.data.meta.totalRowCount)
                setLoading(false);
            });
    }

    const updateSupplier = (supplier) => {
        setSaving(true);
        fetch(`/suppliers/${supplier._id?.$oid}`, "PUT", supplier,
            () => {
                setSaving(false);
                refreshSuppliers();
            },
            (error) => {
                setSaving(false);
                setErrorMessage(data.t.read("failed_to_update_supplier"));
            }
        );
    }

    const openNewSupplierModal = () => {
        setUpdatedSupplier({
            names: [""],
            zipcode: "",
            id_client: data.id_client,
            country: "",
            active: true,
            need_moderation: false,
            TaxCode: data.config.default_tax_code,
            IsBillable: false,
            payment_terms: 30
        });
        setShowSupplierModal(true);
    };

    const openUpdateSupplierModal = (supplier) => {
        setUpdatedSupplier({ ...supplier});
        setShowSupplierModal(true);
    };

    const cancelSupplierModal = () => {
        setUpdatedSupplier({});
        setShowSupplierModal(false);
    };

    const deleteSuppliers = () => {
        fetch("/suppliers/", "DELETE", { "supplier_ids": suppliersDeleteSelection },

            () => {
                refreshSuppliers();
                setSuppliersDeleteSelection([])
                setShowDeleteSupplierModal(false)
            },
            (error) => {
                setErrorMessage(data.t.read("failed_to_delete"))
                setLoading(false);
            }
        );
    }

    const cancelDeleteSuppliersModal = () => {
        setShowDeleteSupplierModal(false);
        setSuppliersDeleteSelection([])
    }

    const prepareImport = (f) => {
        if (f.length > 0) {
            setImportFile(f[0])
        }
    }

    const processImport = () => {
        if (importFile != null) {
            setErrorMessage(null);
            setSupplierImportResult(null);
            const formData = new FormData();
            formData.append("csv", importFile);

            fetch("/suppliers/import", "POST", formData,
                response => {
                    setShowImportModal(false);
                    setImportFile(null);
                    const body = response?.data ?? {};
                    if (body.status) {
                        // New structured report (XPEN-3145-004) — success or partial_success, both 2xx.
                        setSupplierImportResult(body);
                    } else if (body.message && String(body.message).trim().toUpperCase() !== "OK") {
                        // Old shape: today's backend always returns "OK", but a real message is still an error.
                        setErrorMessage(body.message);
                    }
                    refreshSuppliers();
                },
                (e) => {
                    setShowImportModal(false);
                    setImportFile(null);
                    const body = e?.response?.data ?? {};
                    if (body.status || body.summary || body.errors?.length > 0 || body.alerts?.length > 0) {
                        setSupplierImportResult(body);
                        refreshSuppliers();
                    } else {
                        setErrorMessage(data.t.read("suppliers_import_failed"));
                    }
                },
                true)
        }
    }

    const fetchAllSuppliersForExport = () => new Promise((resolve, reject) => {
        const pageSize = 100;
        const filters = [{ id: "id_client", value: data.id_client }];
        const rows = [];

        const fetchPage = (start = 0) => {
            const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters))}&sorting=${encodeURIComponent(JSON.stringify(sorting ?? []))}&start=${encodeURIComponent(start)}&page_size=${pageSize}`;
            fetch(`/suppliers/${searchData}`, "GET", {}, response => {
                const fetchedSuppliers = response.data.suppliers ?? [];
                rows.push(...fetchedSuppliers);
                const total = response.data.meta?.totalRowCount ?? rows.length;
                if (rows.length < total && fetchedSuppliers.length > 0) {
                    fetchPage(start + fetchedSuppliers.length);
                    return;
                }
                resolve(rows);
            }, reject);
        };

        fetchPage();
    });

    const exportSuppliersToExcel = async () => {
        setExporting(true);
        try {
            const exportedSuppliers = await fetchAllSuppliersForExport();
            downloadRowsAsXlsx(
                exportedSuppliers.map((supplier) => adminSupplierExportRow(supplier, data.client_name)),
                `suppliers-${data.client_name || data.id_client || "selected-client"}.xlsx`,
                "Suppliers"
            );
        } catch (error) {
            setErrorMessage(data.t.read("failed_to_export_to_excel"));
        } finally {
            setExporting(false);
        }
    };

    const isAllSuppliersSelected = () => {
        if (!suppliers || !suppliers.length) {
            return false;
        }
        return suppliers.every(supplier => suppliersDeleteSelection.includes(supplier._id?.$oid));
    }

    const isSupplierSelected = (supplier) => {
        return suppliersDeleteSelection.includes(supplier._id?.$oid);
    }

    const toggleAllSuppliersSelected = () => {
        setSuppliersDeleteSelection(isAllSuppliersSelected() ?
            suppliersDeleteSelection.filter(id => !suppliers.some(supplier => supplier._id?.$oid === id))
            :
            [...suppliersDeleteSelection, ...suppliers.map((supplier) => supplier._id?.$oid).filter((id) => !suppliersDeleteSelection.includes(id))]
        );
    }

    const sortTable = (name) => {
        const sorter = sorting.find(s => s.id === name)
        if (sorter === undefined)
            return setSorting([{ "id": name, "desc": true }])

        if (sorter.desc)
            return setSorting([{ "id": name, "desc": false }])

        else
            return setSorting([])
    }

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (rows) => {
        setPage(1)
        setPageSize(rows)
    }

    //pagination logic
    const indexOfFirstItem = (page - 1) * pageSize;
    const indexOfLastItem = indexOfFirstItem + suppliers.length - 1;
    const totalPages = Math.ceil(totalRowCount / pageSize);
    const emptyRowsCount = Math.max(0, pageSize - suppliers.length);
    const emptyRows = Array(emptyRowsCount).fill({});

    return !loading ?
                    <div>
                        <div>
                            <div className='pagination-row'>
                                <FilterInputField
                                    filterValue={filterValue} setFilterValue={setFilterValue} placeholder={""}>
                                </FilterInputField>
                                <PaginationControls
                                    page={page}
                                    totalPages={totalPages}
                                    indexOfFirstItem={indexOfFirstItem}
                                    indexOfLastItem={indexOfLastItem}
                                    handlePageChange={handlePageChange}
                                    totalItems={totalRowCount}
                                    data={data}
                                    handleRowsPerPageChange={handleRowsPerPageChange}
                                    rowsPerPage={pageSize}
                                ></PaginationControls>
                            </div>
                            <div className="d-flex justify-content-end align-items-center px-4 py-2">
                                <div className="d-flex">
                                    <button
                                        type="button"
                                        className=' pointer btn btn-secondary btn-sm ml-2'
                                        disabled={saving || suppliersDeleteSelection.length === 0}
                                        onClick={() => setShowDeleteSupplierModal(true)}
                                    >
                                        {data.t.read("delete") + " (" + suppliersDeleteSelection.length + ")"}
                                    </button>
                                    <button
                                        onClick={exportSuppliersToExcel}
                                        type="button"
                                        disabled={exporting}
                                        className='btn btn-secondary btn-sm ml-2'>
                                        {exporting ? data.t.read("exporting") : data.t.read("export")}
                                    </button>
                                    {canImportSuppliers && (
                                        <button
                                            onClick={() => setShowImportModal(!showImportModal)}
                                            type="button"
                                            className='btn btn-primary btn-sm ml-2'>
                                            {data.t.read("import")}
                                        </button>
                                    )}
                                    {canCreateSuppliers && (
                                        <button
                                            onClick={() => openNewSupplierModal()} type="button"
                                            className='btn btn-primary btn-sm ml-2'>
                                            {data.t.read("add")}
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="mb-2 card rounded-lg admin-table">
                                <table className='table table-hover font-small'>
                                    <thead>

                                    <tr>
                                        <th className="">
                                            <Checkbox disabled={!suppliers || !suppliers.length}
                                                      isChecked={isAllSuppliersSelected()}
                                                      onChange={toggleAllSuppliersSelected}/>
                                        </th>
                                        <th
                                            className="pointer col-3"
                                            onClick={() => sortTable("names")}>
                                            {data.t.read("name")}
                                            {sorting.find(s => s.id === "names") &&
                                                <FontAwesomeIcon
                                                    className={loading ? "infinite-rotate faded ml-2 fa-xs" : "ml-2 fa-xs"}
                                                    icon={sorting.find(s => s.id === "names").desc ? faArrowDown : faArrowUp}/>}
                                        </th>
                                        <th
                                            className="pointer col-1"
                                            onClick={() => sortTable("zipcode")}>
                                            {data.t.read("supplier_table.postal_code")}
                                            {sorting.find(s => s.id === "zipcode") &&
                                                <FontAwesomeIcon
                                                    className={loading ? "infinite-rotate faded ml-2 fa-xs" : "ml-2 fa-xs"}
                                                    icon={sorting.find(s => s.id === "zipcode").desc ? faArrowDown : faArrowUp}/>}
                                        </th>
                                        <th
                                            className="pointer col-1"
                                            onClick={() => sortTable("country")}>
                                            {data.t.read("country")}
                                            {sorting.find(s => s.id === "country") &&
                                                <FontAwesomeIcon
                                                    className={loading ? "infinite-rotate faded ml-2 fa-xs" : "ml-2 fa-xs"}
                                                    icon={sorting.find(s => s.id === "country").desc ? faArrowDown : faArrowUp}/>}
                                        </th>
                                        <th
                                            className="pointer col-1">
                                            {data.t.read("supplier_table.created_by")}
                                            {sorting.find(s => s.id === "name") &&
                                                <FontAwesomeIcon
                                                    className={loading ? "infinite-rotate faded ml-2 fa-xs" : "ml-2 fa-xs"}
                                                    icon={sorting.find(s => s.id === "name").desc ? faArrowDown : faArrowUp}/>}
                                        </th>
                                        <th
                                            className="pointer col-2"
                                            onClick={() => sortTable("created_at")}>
                                            {data.t.read("supplier_table.created_on")}
                                            {sorting.find(s => s.id === "created_at") &&
                                                <FontAwesomeIcon
                                                    className={loading ? "infinite-rotate faded ml-2 fa-xs" : "ml-2 fa-xs"}
                                                    icon={sorting.find(s => s.id === "created_at").desc ? faArrowDown : faArrowUp}/>}
                                        </th>
                                        <th
                                            className="pointer col-2"
                                            onClick={() => sortTable("updated_at")}>
                                            {data.t.read("supplier_table.last_modified_on")}
                                            {sorting.find(s => s.id === "updated_at") &&
                                                <FontAwesomeIcon
                                                    className={loading ? "infinite-rotate faded ml-2 fa-xs" : "ml-2 fa-xs"}
                                                    icon={sorting.find(s => s.id === "updated_at").desc ? faArrowDown : faArrowUp}/>}
                                        </th>
                                        <th className="text-center pointer" onClick={() => sortTable("active")}>
                                            {data.t.read("supplier_table.active")}
                                            {sorting.find(s => s.id === "active") &&
                                                <FontAwesomeIcon
                                                    className={loading ? "infinite-rotate faded ml-2 fa-xs" : "ml-2 fa-xs"}
                                                    icon={sorting.find(s => s.id === "active").desc ? faArrowDown : faArrowUp}/>}
                                        </th>
                                        <th className="pointer">
                                            Actions
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {
                                        suppliers.map(supplier => {
                                            return <tr key={supplier._id?.$oid}
                                                       className={supplier.need_moderation ? "bg-info" : ""}>
                                                <td>

                                                    <Checkbox isChecked={isSupplierSelected(supplier)}
                                                              onChange={() => isSupplierSelected(supplier) ? setSuppliersDeleteSelection(suppliersDeleteSelection.filter(x => x !== supplier._id?.$oid)) : setSuppliersDeleteSelection([...suppliersDeleteSelection, supplier._id?.$oid])}/>
                                                </td>
                                                <td onClick={() => openUpdateSupplierModal(supplier)}>
                                                    {supplier.names[0]}
                                                </td>
                                                <td onClick={() => openUpdateSupplierModal(supplier)}>
                                                    {supplier.zipcode}
                                                </td>
                                                <td onClick={() => openUpdateSupplierModal(supplier)}>
                                                    {supplier.country}
                                                </td>
                                                <td onClick={() => openUpdateSupplierModal(supplier)}>
                                                    {data.client_name}
                                                </td>
                                                <td onClick={() => openUpdateSupplierModal(supplier)}>
                                                    {supplier.created_at ? Intl.DateTimeFormat(`${data.t.lang}-US`, {
                                                        year: 'numeric',
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        second: '2-digit',
                                                        hour12: false,
                                                    }).format(new Date(supplier.created_at?.$date)) : ""}
                                                </td>
                                                <td onClick={() => openUpdateSupplierModal(supplier)}>
                                                    {supplier.updated_at ? Intl.DateTimeFormat(`${data.t.lang}-US`, {
                                                        year: 'numeric',
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        second: '2-digit',
                                                        hour12: false,
                                                    }).format(new Date(supplier.updated_at?.$date)) : ""}
                                                </td>
                                                <td className="text-center">
                                                    <Switch
                                                        className="m-0"
                                                        checked={supplier?.active ?? true}
                                                        width={65}
                                                        onColor="#F39800"
                                                        onHandleColor="#2693e6"
                                                        boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                                                        activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                                                        onChange={(active) => updateSupplier({ ...supplier, active })}
                                                    />
                                                </td>
                                                <td>
                                                    {<FontAwesomeIcon className="mr-3 ml-2"
                                                                      onClick={() => openUpdateSupplierModal(supplier)}
                                                                      icon={faPencilAlt}/>}
                                                    {<FontAwesomeIcon style={{color: 'red'}}
                                                                      onClick={() => {
                                                                          setSuppliersDeleteSelection([supplier._id?.$oid]);
                                                                          setShowDeleteSupplierModal(true);
                                                                      }}
                                                                      icon={faWindowClose}/>}
                                                </td>
                                            </tr>

                                        })
                                    }
                                    {
                                        emptyRows.map((_, idx) => (
                                            <tr key={`empty-${idx}`}>
                                                <td colSpan="9">&nbsp;</td>
                                            </tr>
                                        ))
                                    }
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        {errorMessage != null && (
                            <Modal title="Attention" okText={"OK"} okAction={() => setErrorMessage(null)}>

                                <p style={{whiteSpace: 'pre-line'}} className='text-danger'>{errorMessage}</p>
                            </Modal>
                        )}
                        {successMessage != null && (
                            <Modal title="Attention" okText={"OK"} okAction={() => setSuccessMessage(null)}>
                                {successMessage}
                            </Modal>
                        )}
                        {showImportModal && (
                            <Modal width="50vw" title={data.t.read("importation")}
                                   cancelAction={() => setShowImportModal(false)} okAction={() => processImport()}
                                   okText={data.t.read("import")} cancelText={data.t.read("cancel")}>
                                <div>
                                    <SupplierImportHelp data={data} variant="admin" />
                                    <div className='mt-2 mb-2'>
                                        <input className='form-control-file' type="file" id="import-csv-file"
                                               data-testid="import-csv-file"
                                               onChange={f => prepareImport(f.target.files)}/>
                                    </div>
                                </div>
                            </Modal>
                        )}
                        {supplierImportResult && (
                            <ImportResultModal
                                data={data}
                                result={supplierImportResult}
                                summarySpec={supplierImportSummarySpec}
                                onClose={() => setSupplierImportResult(null)}
                            />
                        )}
                        {
                            showSupplierModal && <SupplierForm supplier={updatedSupplier}
                             cancelSupplierModal={cancelSupplierModal} setShowSupplierModal={setShowSupplierModal}
                            callerCallback={supplier => {
                                refreshSuppliers();
                            }}
                              />
                        }
                        {showDeleteSupplierModal && (
                            <Modal
                                title={"Delete"}
                                okAction={deleteSuppliers}
                                cancelAction={cancelDeleteSuppliersModal}
                                okText={data.t.read("delete")}
                                cancelText={data.t.read("cancel")}>
                                <p className='text-danger'>{suppliersDeleteSelection.length === 1 ? data.t.read("supplier_table.delete_supplier") : data.t.read("supplier_table.delete_suppliers")}</p>
                            </Modal>
                        )}
                    </div>
                    :
                    <div className="d-flex loading-container w-100">

                        <FontAwesomeIcon className="infinite-rotate" icon={faSpinner}/>

                    </div>
}

export default inject('data')(observer(AdminSuppliers));
